import { format } from 'date-fns';
import { ExceptionLog, ExceptionType } from '../types/exception-log';

/**
 * Date format constants
 */
export const DATE_TIME_FORMAT = 'MMM dd, yyyy \'at\' h:mm a';
export const DATE_FORMAT = 'MMM dd, yyyy';

/**
 * Formats old/new values for display in tooltip
 */
export function formatExceptionValue(
  value: Record<string, any> | null | undefined,
  exceptionType: ExceptionType,
  isOld: boolean
): string {
  if (!value) {
    // A RESCHEDULE log's new_value stays empty until the replacement session
    // is booked (the server fills it on the next booking of the same usage).
    if (exceptionType === ExceptionType.RESCHEDULE && !isOld) {
      return 'Awaiting rebooking';
    }
    return 'N/A';
  }

  switch (exceptionType) {
    case ExceptionType.EXPIRY_EXTENSION:
      if (value.end_date) {
        return format(new Date(value.end_date), DATE_FORMAT);
      }
      return 'N/A';

    case ExceptionType.EXTRA_SESSION:
      // For EXTRA_SESSION, backend stores total_session_quantity in both old_value and new_value
      // old_value: { total_session_quantity: old_session_count }
      // new_value: { total_session_quantity: new_session_count }
      const sessionCount = value.total_session_quantity;
      if (typeof sessionCount === 'number') {
        return `${sessionCount} sessions`;
      }
      return 'N/A';

    case ExceptionType.RESCHEDULE:
      // For RESCHEDULE, backend stores start_time in both old_value and new_value
      // old_value: { start_time: old_session_start_time }
      // new_value: { start_time: new_session_start_time }
      if (value.start_time) {
        return format(new Date(value.start_time), DATE_TIME_FORMAT);
      }
      return 'N/A';

    case ExceptionType.MANUAL_ADJUSTMENT:
    case ExceptionType.SYSTEM_OVERRIDE:
    default:
      // Try to find common fields
      if (value.total_session_quantity !== undefined) {
        return `${value.total_session_quantity} sessions`;
      }
      if (value.end_date) {
        return format(new Date(value.end_date), DATE_FORMAT);
      }
      if (value.start_time) {
        return format(new Date(value.start_time), DATE_TIME_FORMAT);
      }
      // Fallback to JSON for complex objects (truncated)
      const jsonStr = JSON.stringify(value);
      return jsonStr.length > 50 ? jsonStr.substring(0, 50) + '...' : jsonStr;
  }
}

/**
 * Formats exception type name for display
 */
export function formatExceptionTypeName(exceptionType: ExceptionType): string {
  switch (exceptionType) {
    case ExceptionType.EXTRA_SESSION:
      return 'Extra Session';
    case ExceptionType.EXPIRY_EXTENSION:
      return 'Expiry Extension';
    case ExceptionType.RESCHEDULE:
      return 'Reschedule';
    case ExceptionType.MANUAL_ADJUSTMENT:
      return 'Manual Adjustment';
    case ExceptionType.SYSTEM_OVERRIDE:
      return 'System Override';
    default:
      return exceptionType;
  }
}

/**
 * Calculates days extended for EXPIRY_EXTENSION
 */
export function calculateDaysExtended(
  oldValue: Record<string, any> | null | undefined,
  newValue: Record<string, any> | null | undefined
): number | null {
  if (!oldValue?.end_date || !newValue?.end_date) {
    return null;
  }

  const oldDate = new Date(oldValue.end_date);
  const newDate = new Date(newValue.end_date);
  const daysDiff = Math.ceil((newDate.getTime() - oldDate.getTime()) / (1000 * 60 * 60 * 24));
  
  return daysDiff;
}

/**
 * Formats exception type heading with extended days for EXPIRY_EXTENSION
 */
export function formatExceptionHeading(log: ExceptionLog): string {
  const baseName = formatExceptionTypeName(log.exception_type);

  if (log.exception_type === ExceptionType.EXPIRY_EXTENSION) {
    const daysExtended = calculateDaysExtended(log.old_value, log.new_value);
    if (daysExtended !== null) {
      return `${baseName} (extended by ${daysExtended} ${daysExtended === 1 ? 'day' : 'days'})`;
    }
  }

  return baseName;
}

